import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { questionBanks, testSchemas } from '../api';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmDialog';
import { stripEmoji } from '../utils/text';

function QuestionBanks() {
  const [banks, setBanks] = useState([]);
  const [schemas, setSchemas] = useState([]);
  const [showUpload, setShowUpload] = useState(false);
  const [name, setName] = useState('');
  const [csvFile, setCsvFile] = useState(null);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingBanks, setLoadingBanks] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [requiredImages, setRequiredImages] = useState([]);
  const [uploadedImageNames, setUploadedImageNames] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [openCategories, setOpenCategories] = useState({});
  const [cloningId, setCloningId] = useState(null);
  const [addedBank, setAddedBank] = useState(null); // { id, name } — только что добавленная база
  const navigate = useNavigate();
  const showToast = useToast();
  const confirm = useConfirm();

  useEffect(() => {
    loadData();
    loadCatalog();
  }, []);

  const loadData = async () => {
    setLoadingBanks(true);
    setLoadError(false);
    try {
      const [banksResponse, schemasResponse] = await Promise.all([
        questionBanks.getAll(),
        testSchemas.getAll(),
      ]);
      setBanks(banksResponse.data || []);
      setSchemas(schemasResponse.data || []);
    } catch (err) {
      console.error('Failed to load data', err);
      // Устанавливаем пустые массивы при ошибке
      setBanks([]);
      setSchemas([]);
      setLoadError(true);
    } finally {
      setLoadingBanks(false);
    }
  };

  const loadCatalog = async () => {
    try {
      const response = await questionBanks.getCatalog();
      setCatalog(response.data || []);
    } catch (err) {
      console.error('Failed to load catalog', err);
      setCatalog([]);
    }
  };

  const handleClone = async (catalogBank) => {
    setCloningId(catalogBank.id);
    try {
      const res = await questionBanks.cloneBank(catalogBank.id);
      await loadData();
      const newBank = res.data || {};
      setAddedBank({ id: newBank.id, name: newBank.name || catalogBank.name });
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Не удалось добавить базу';
      showToast(detail, 'error');
    } finally {
      setCloningId(null);
    }
  };

  const CATEGORY_ORDER = ['Бизнес и профессии', 'Языки программирования', 'Прочее'];

  const groupCatalogByCategory = () => {
    const groups = {};
    for (const cb of catalog) {
      const cat = cb.category || 'Прочее';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(cb);
    }
    // Сортируем по заданному порядку, незнакомые категории — в конец по алфавиту
    const known = CATEGORY_ORDER.filter((c) => groups[c]);
    const extra = Object.keys(groups)
      .filter((c) => !CATEGORY_ORDER.includes(c))
      .sort((a, b) => a.localeCompare(b, 'ru'));
    return [...known, ...extra].map((cat) => [cat, groups[cat]]);
  };

  const toggleCategory = (cat) => {
    setOpenCategories((prev) => ({ ...prev, [cat]: !prev[cat] }));
  };

  const getSchemasCountForBank = (bankId) => {
    return schemas.filter(schema => schema.question_bank_id === bankId).length;
  };

  const parseCSVForImages = async (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target.result;
        const lines = text.split('\n');
        const imageNames = new Set();
        
        // Skip header, process all lines
        for (let i = 1; i < lines.length; i++) {
          const line = lines[i].trim();
          if (!line) continue;
          
          // Split by semicolon (CSV delimiter)
          const columns = line.split(';');
          // Last column is image name
          const imageName = columns[columns.length - 1]?.trim();
          
          if (imageName && imageName !== '') {
            imageNames.add(imageName);
          }
        }
        
        resolve(Array.from(imageNames));
      };
      reader.readAsText(file);
    });
  };

  const handleCSVChange = async (e) => {
    const file = e.target.files[0];
    setCsvFile(file);
    
    if (file) {
      const imageNames = await parseCSVForImages(file);
      setRequiredImages(imageNames);
      setUploadedImageNames([]);
      setImages([]);
    }
  };

  const handleImagesChange = (e) => {
    const files = Array.from(e.target.files);
    setImages(files);
    setUploadedImageNames(files.map(f => f.name));
  };

  const allImagesUploaded = () => {
    if (requiredImages.length === 0) return true;
    return requiredImages.every(reqImg => uploadedImageNames.includes(reqImg));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Валидация перед отправкой
    if (!name || name.trim() === '') {
      showToast('Пожалуйста, укажите название базы вопросов', 'error');
      return;
    }

    if (!csvFile) {
      showToast('Пожалуйста, выберите CSV файл', 'error');
      return;
    }

    if (requiredImages.length > 0 && !allImagesUploaded()) {
      const missing = requiredImages.filter(img => !uploadedImageNames.includes(img));
      showToast(`Пожалуйста, загрузите все требуемые изображения:\n${missing.join('\n')}`, 'error');
      return;
    }
    
    setLoading(true);

    const formData = new FormData();
    formData.append('name', name.trim());
    formData.append('csv_file', csvFile);
    images.forEach((image) => {
      formData.append('images', image);
    });

    try {
      const response = await questionBanks.create(formData);
      
      const newBankId = response.data.id;
      
      // Закрываем форму и очищаем поля
      setShowUpload(false);
      setName('');
      setCsvFile(null);
      setImages([]);
      setRequiredImages([]);
      setUploadedImageNames([]);
      
      // Обновляем данные сразу после создания
      await loadData();
      
      // Перенаправляем на страницу тест-схем
      navigate('/test-schemas');
    } catch (err) {
      console.error('Ошибка при создании базы:', err);
      
      let errorMessage = 'Ошибка загрузки базы вопросов';
      
      if (err.response?.data) {
        if (typeof err.response.data === 'string') {
          errorMessage = err.response.data;
        } else if (err.response.data.detail) {
          errorMessage = err.response.data.detail;
        } else if (err.response.data.message) {
          errorMessage = err.response.data.message;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      // Если ошибка содержит детали валидации (многострочная), показываем её как есть
      const hasValidationDetails = errorMessage.includes('Строка') || errorMessage.includes('\n');
      
      if (hasValidationDetails) {
        showToast(`Обнаружены ошибки в CSV файле:\n\n${errorMessage}\n\nИсправьте ошибки и попробуйте снова.`, 'error');
      } else {
        showToast(`${errorMessage}\n\nПроверьте:\n- Формат CSV файла\n- Кодировку файла (должна быть UTF-8)\n- Все ли колонки заполнены\n- Консоль браузера для деталей`, 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  // Имена уже добавленных баз — чтобы не предлагать добавить дубль из каталога
  const ownedBankNames = new Set(banks.map((b) => b.name));

  return (
    <>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 md:mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Базы вопросов</h1>
        <button 
          onClick={() => setShowUpload(!showUpload)}
          className="w-full sm:w-auto bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition-all hover:-translate-y-0.5 text-sm md:text-base"
        >
          {showUpload ? 'Отмена' : 'Загрузить новую базу'}
        </button>
      </div>

      {addedBank && (
        <div className="mb-6 md:mb-8 bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-xl p-4 md:p-5">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex-1 min-w-0">
              <p className="text-sm md:text-base font-semibold text-gray-900">
                База «{addedBank.name}» добавлена
              </p>
              <p className="text-xs md:text-sm text-gray-600 mt-1">
                Остался один шаг: настройте тест-схему — и можно приглашать кандидатов на проверку.
              </p>
              <div className="flex items-center gap-2 mt-3 text-xs font-medium flex-wrap">
                <span className="flex items-center gap-1 text-green-700"><span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-green-100 text-green-700"><svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg></span> База добавлена</span>
                <svg className="w-3 h-3 text-gray-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5l7 7-7 7"/></svg>
                <span className="flex items-center gap-1 text-blue-700"><span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-xs font-bold">2</span> Тест-схема</span>
                <svg className="w-3 h-3 text-gray-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5l7 7-7 7"/></svg>
                <span className="flex items-center gap-1 text-gray-400"><span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-xs font-bold">3</span> Кандидаты</span>
              </div>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button
                onClick={() => navigate(`/test-schemas/create/${addedBank.id}`)}
                className="bg-blue-600 text-white px-4 py-2.5 rounded-lg font-semibold text-sm hover:bg-blue-700 transition-colors whitespace-nowrap min-h-[44px]"
              >
                Настроить тест-схему
              </button>
              <button
                onClick={() => setAddedBank(null)}
                className="px-3 py-2.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-white/70 transition-colors min-h-[44px]"
              >
                Позже
              </button>
            </div>
          </div>
        </div>
      )}

      {showUpload && (
        <div className="bg-white p-4 md:p-8 rounded-xl shadow-sm border border-gray-200 mb-6 md:mb-8">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6">Загрузка базы вопросов</h2>
          
          <div className="bg-blue-50 border-2 border-blue-500 p-4 rounded-lg mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
              <h4 className="text-blue-700 flex items-center font-semibold">
                Формат CSV файла
              </h4>
              <a
                href="/template.zip"
                download="template.zip"
                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors shadow-sm hover:shadow-md"
              >
                Скачать шаблон
              </a>
            </div>
            <div className="text-blue-900 text-sm leading-relaxed">
              <p className="mb-2">
                <strong>Колонки:</strong> Название секции; Вопрос; Тип; Варианты ответов; Ответ; Вес; Пояснение веса; <strong>Время ответа (сек)</strong>; Название картинки
              </p>
              <p className="mb-2">
                <strong>Время:</strong> Указывается в секундах (30, 45, 60, 120, 180 и т.д.)
              </p>
              <p>
                <strong>Изображения:</strong> Имена файлов должны <strong>точно совпадать</strong> с именами в CSV. 
                Например: <code className="bg-white px-1.5 py-0.5 rounded">1.jpg</code> в CSV &rarr; файл <code className="bg-white px-1.5 py-0.5 rounded">1.jpg</code>
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
            <div>
              <label className="block mb-2 text-sm md:text-base text-gray-700 font-medium">Название базы</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="Например: iOS Developer Screening"
                className="w-full px-4 py-3 text-sm md:text-base border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-600 transition-colors"
              />
            </div>
            <div>
              <label className="block mb-2 text-sm md:text-base text-gray-700 font-medium">CSV файл с вопросами</label>
              <input
                type="file"
                accept=".csv"
                onChange={handleCSVChange}
                required
                className="w-full px-3 md:px-4 py-2 md:py-3 text-sm border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 transition-colors"
              />
              {csvFile && (
                <div className="mt-2">
                  <p className="text-green-600 text-sm font-semibold">
                    Выбран: {csvFile.name}
                  </p>
                  {requiredImages.length > 0 && (
                    <p className="text-gray-600 text-xs mt-1">
                      Найдено {requiredImages.length} изображений в CSV
                    </p>
                  )}
                </div>
              )}
            </div>
            
            {requiredImages.length > 0 && (
              <div>
                <label className="block mb-2 text-sm md:text-base text-gray-700 font-medium">
                  Изображения {!allImagesUploaded() && <span className="text-red-600">*</span>}
                </label>
                
                <div className={`bg-gray-50 p-2 md:p-3 rounded-lg mb-3 border-2 ${allImagesUploaded() ? 'border-green-500' : 'border-red-500'}`}>
                  <p className="text-xs md:text-sm font-semibold mb-2 text-gray-900">
                    Требуемые изображения:
                  </p>
                  <div className="flex flex-wrap gap-1.5 md:gap-2">
                    {requiredImages.map((imgName, idx) => {
                      const isUploaded = uploadedImageNames.includes(imgName);
                      return (
                        <span
                          key={idx}
                          className={`px-2 md:px-3 py-1 md:py-1.5 rounded-md text-xs font-medium flex items-center gap-1 ${
                            isUploaded 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {isUploaded ? (
                            <svg className="w-3 h-3 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
                          ) : (
                            <svg className="w-3 h-3 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
                          )} <span className="truncate max-w-[120px] md:max-w-none">{imgName}</span>
                        </span>
                      );
                    })}
                  </div>
                  {!allImagesUploaded() && (
                    <p className="mt-2 text-xs text-red-600 font-semibold">
                      Загрузите все требуемые изображения
                    </p>
                  )}
                </div>

                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImagesChange}
                  className="w-full px-3 md:px-4 py-2 md:py-3 text-sm border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 transition-colors"
                />
                {images.length > 0 && (
                  <p className="mt-2 text-gray-600 text-sm">
                    Выбрано файлов: {images.length}
                  </p>
                )}
              </div>
            )}
            
            {requiredImages.length === 0 && csvFile && (
              <div className="bg-green-50 border-2 border-green-500 p-3 rounded-lg mb-5">
                <p className="text-sm text-green-800 font-semibold m-0">
                  В CSV нет ссылок на изображения
                </p>
                <p className="text-xs text-green-700 mt-1 m-0 opacity-80">
                  Можно продолжить загрузку без изображений
                </p>
              </div>
            )}

            <button 
              type="submit" 
              disabled={loading || !name || !csvFile || (requiredImages.length > 0 && !allImagesUploaded())}
              className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none disabled:hover:translate-y-0 hover:-translate-y-0.5"
              title={
                !name ? 'Укажите название базы' :
                !csvFile ? 'Выберите CSV файл' :
                requiredImages.length > 0 && !allImagesUploaded() ? 'Загрузите все требуемые изображения' : 
                'Загрузить базу вопросов'
              }
            >
              {loading ? 'Загрузка...' : 'Загрузить'}
            </button>
          </form>
        </div>
      )}

      <div className="mt-6 md:mt-8 mb-8 md:mb-10">
        {banks.length > 0 && (
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">
            Ваши базы <span className="text-gray-400 font-semibold">({banks.length})</span>
          </h2>
        )}
        {loadingBanks ? (
          <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div></div>
        ) : loadError ? (
          <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 flex items-center justify-between gap-4"><span>Не удалось загрузить данные</span><button onClick={loadData} className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-red-600 text-white hover:bg-red-700 flex-shrink-0">Повторить</button></div>
        ) : banks.length === 0 ? (
          <div className="bg-white p-4 md:p-6 rounded-xl shadow-sm border border-gray-200">
            <p className="text-gray-600 text-sm md:text-base">Пока нет загруженных баз вопросов. Добавьте готовую из каталога ниже или загрузите свою.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {banks.map((bank) => {
              const schemasCount = getSchemasCountForBank(bank.id);
              const questionsCount = bank.questions_count || 0;
              return (
                <div key={bank.id} className={`bg-white p-4 md:p-6 rounded-xl shadow-sm border transition-all hover:shadow-md hover:-translate-y-1 ${addedBank?.id === bank.id ? 'border-green-400 ring-2 ring-green-300' : 'border-gray-200'}`}>
                  <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-3 truncate">{bank.name}</h3>
                  <p className="text-sm md:text-base text-gray-600 mt-2">
                    Вопросов: <strong className="text-blue-600">{questionsCount}</strong>
                  </p>
                  <p className="text-gray-400 mt-2 text-xs truncate" title={bank.csv_filename}>
                    CSV: {bank.csv_filename}
                  </p>

                  <div className="flex items-center gap-3 mt-4 pt-4 border-t border-gray-200">
                    <div className="flex-1">
                      <p className={`text-xs md:text-sm font-semibold ${schemasCount > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                        {schemasCount} {schemasCount === 1 ? 'тест-схема' : schemasCount > 1 && schemasCount < 5 ? 'тест-схемы' : 'тест-схем'}
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={() => {
                        if (schemasCount > 0) {
                          navigate('/test-schemas');
                        } else {
                          navigate(`/test-schemas/create/${bank.id}`);
                        }
                      }}
                      className="flex-1 bg-blue-600 text-white px-3 md:px-4 py-2 rounded-lg font-semibold text-xs md:text-sm hover:shadow-lg transition-all hover:-translate-y-0.5 flex items-center justify-center min-h-[44px] md:min-h-[36px]"
                    >
                      {schemasCount > 0 ? 'Управление схемами' : '+ Создать тест-схему'}
                    </button>

                    <button
                      onClick={async () => {
                        if (schemasCount > 0) {
                          showToast(`Невозможно удалить базу: используется в ${schemasCount} тест-схемах. Сначала удалите все связанные схемы.`, 'error');
                          return;
                        }

                        if (!(await confirm({
                          title: `Удалить базу вопросов «${bank.name}»?`,
                          message: `Будут удалены:\n- База вопросов\n- ${questionsCount} вопросов\n\nЭто действие нельзя отменить.`,
                          confirmText: 'Удалить',
                          danger: true,
                        }))) return;

                        try {
                          await questionBanks.delete(bank.id);
                          showToast(`База вопросов «${bank.name}» удалена`);
                          loadData();
                        } catch (err) {
                          showToast('Ошибка удаления: ' + (err.response?.data?.detail || err.message), 'error');
                        }
                      }}
                      className={`w-11 h-11 md:w-9 md:h-9 flex items-center justify-center text-base rounded-lg transition-all flex-shrink-0 ${
                        schemasCount > 0
                          ? 'bg-gray-200 text-gray-500 cursor-not-allowed opacity-60'
                          : 'bg-red-500 text-white hover:bg-red-600 hover:scale-105'
                      }`}
                      title={schemasCount > 0 ? 'Сначала удалите все тест-схемы, использующие эту базу' : 'Удалить базу вопросов'}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>

                  <p className="text-xs text-gray-400 mt-4 text-center">
                    Загружено: {new Date(bank.uploaded_at).toLocaleDateString('ru-RU')}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {catalog.length > 0 && (
        <div className="mb-8 md:mb-10">
          <div className="flex items-center gap-2 mb-4">
            <h2 className="text-xl md:text-2xl font-bold text-gray-900">Каталог готовых баз</h2>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Готовые базы вопросов по специальностям. Добавьте подходящую к себе одним кликом и сразу создавайте тест-схемы.
          </p>
          <div className="flex flex-col gap-3">
            {groupCatalogByCategory().map(([category, banksInCat], idx) => {
              const isOpen = openCategories[category] ?? (idx === 0);
              return (
                <div key={category} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <button
                    onClick={() => toggleCategory(category)}
                    aria-expanded={isOpen}
                    className="w-full flex items-center justify-between gap-3 px-4 md:px-5 py-3.5 text-left hover:bg-gray-50 transition-colors"
                  >
                    <span className="flex items-center gap-2.5 min-w-0">
                      <svg
                        className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-90' : ''}`}
                        fill="none" stroke="currentColor" viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                      <span className="text-sm md:text-base font-semibold text-gray-900 truncate">{category}</span>
                    </span>
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 flex-shrink-0">
                      {banksInCat.length}
                    </span>
                  </button>

                  {isOpen && (
                    <div className="border-t border-gray-100 divide-y divide-gray-100">
                      {banksInCat.map((cb) => (
                        <div
                          key={cb.id}
                          className="flex items-center gap-3 px-4 md:px-5 py-3 hover:bg-gray-50 transition-colors"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-medium text-gray-900">{stripEmoji(cb.name).replace(/\s*—\s*готовая база\s*$/i, '')}</span>
                              <span className="text-xs text-gray-500 whitespace-nowrap">
                                {cb.questions_count}
                              </span>
                            </div>
                            {cb.sections && cb.sections.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1.5">
                                {cb.sections.map((section, sidx) => (
                                  <span
                                    key={sidx}
                                    className="px-1.5 py-0.5 rounded text-[11px] font-medium bg-blue-50 text-blue-700"
                                  >
                                    {stripEmoji(section)}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          {ownedBankNames.has(cb.name) ? (
                            <span className="flex-shrink-0 inline-flex items-center gap-1 text-green-600 text-xs md:text-sm font-semibold px-3 py-2 whitespace-nowrap">
                              <svg className="w-3 h-3 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5"/></svg> Добавлена
                            </span>
                          ) : (
                            <button
                              onClick={() => handleClone(cb)}
                              disabled={cloningId === cb.id}
                              className="flex-shrink-0 bg-blue-600 text-white px-3 py-2 rounded-lg font-semibold text-xs md:text-sm hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[40px] whitespace-nowrap"
                            >
                              {cloningId === cb.id ? '...' : 'Добавить'}
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

    </>
  );
}

export default QuestionBanks;

